import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Eye, ExternalLink, Pause, Play, Trash2, Loader2, Star, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { AdminReviewsDialog } from '@/components/admin/AdminReviewsDialog';
import { ownerListStudios, ownerModerateStudio, ownerDeleteStudio, setActiveStudioId, apiErrorMessage } from '@/lib/api';
import { formatPriceFrom } from '@/lib/studioAccent';
import { ADMIN_PHOTOGRAPHERS as T } from '@/constants/testIds';

const tone = { approved: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200', pending: 'border-[#C9A45C]/25 bg-[#C9A45C]/10 text-[#D7B46A]', suspended: 'border-red-500/25 bg-red-500/10 text-red-200' };
const screeningTone = { high: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200', medium: 'border-[#C9A45C]/25 bg-[#C9A45C]/10 text-[#D7B46A]', low: 'border-red-500/25 bg-red-500/10 text-red-200' };
const decisionTone = {
  recommended: { label: 'Recommended', className: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200' },
  needs_review: { label: 'Needs review', className: 'border-[#C9A45C]/25 bg-[#C9A45C]/10 text-[#D7B46A]' },
  flagged: { label: 'Flagged', className: 'border-red-500/25 bg-red-500/10 text-red-200' },
  unscreened: { label: 'Unscreened', className: 'border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-muted))]' },
};
const iconBtn = 'h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[#1a1a1c]';

/** LXRA owner: approve / suspend / verify Marketplace studios, and jump into any tenant. */
export const AdminPhotographersTab = ({ onViewAs }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null); // { studio, action }
  const [reviewsFor, setReviewsFor] = useState(null);

  const refresh = useCallback(() => ownerListStudios(filter === 'all' ? undefined : filter).then(setData).catch(() => setData({ studios: [], pending_count: 0 })), [filter]);
  useEffect(() => { refresh(); }, [refresh]);

  const moderate = async (studio, payload, okMessage) => {
    setBusy(studio.id);
    try {
      await ownerModerateStudio(studio.id, payload);
      toast.success(okMessage);
      refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update the studio.'));
    } finally {
      setBusy(null);
    }
  };

  const runConfirmed = async () => {
    const { studio, action } = confirm;
    setConfirm(null);
    if (action === 'suspend') await moderate(studio, { status: 'suspended' }, `${studio.name} suspended. Their public page is hidden.`);
    if (action === 'remove') {
      setBusy(studio.id);
      try { await ownerDeleteStudio(studio.id); toast.success('Studio removed.'); refresh(); }
      catch (err) { toast.error(apiErrorMessage(err, 'Could not remove the studio.')); }
      finally { setBusy(null); }
    }
  };

  const viewAs = (studio) => { setActiveStudioId(studio.id); onViewAs?.(studio.id); toast.success(`Now viewing as ${studio.name}.`); };

  return (
    <div data-testid={T.root} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[hsl(var(--lxra-muted))]">
          Marketplace studios. Approve applications, suspend if needed, and award the Verified LXRA badge.
          {data?.pending_count > 0 && <span data-testid={T.pendingCount} className="ml-2 inline-flex h-5 items-center rounded-full bg-amber-400 px-2 text-[10px] font-bold text-[#0A0A0A]">{data.pending_count} pending</span>}
        </p>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid={T.filterSelect} className="w-44 h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
            <SelectItem value="all">All studios</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filter === 'pending' && (
        <p data-testid={T.queueHint} className="text-xs text-[hsl(var(--lxra-muted))] -mt-1">
          Sorted by AI screening score, highest-quality applications first — review the top of the queue before the rest.
        </p>
      )}

      {!data && <Skeleton className="h-64 rounded-2xl bg-[#141416]" />}
      {data && data.studios.length === 0 && (
        <p data-testid={T.empty} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 text-center text-sm text-[hsl(var(--lxra-muted))]">No studios match this filter.</p>
      )}
      {data && data.studios.length > 0 && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid={T.table}>
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                {['Studio', 'Handle', 'Status', 'Screening', 'AI Decision', 'Verified', 'Home base', 'From', 'Data', 'Actions'].map((h) => <TableHead key={h} className={`text-[hsl(var(--lxra-muted))] ${h === 'Actions' ? 'text-right' : ''}`}>{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.studios.map((s) => {
                const isLxra = s.kind === 'lxra';
                const decision = decisionTone[s.decision] || decisionTone.unscreened;
                return (
                  <TableRow key={s.id} data-testid={T.row(s.id)} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                    <TableCell className="text-[hsl(var(--lxra-fg))]">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-[11px] text-[hsl(var(--lxra-muted))]">{s.owner_email}</div>
                    </TableCell>
                    <TableCell className="text-[hsl(var(--lxra-muted))] lxra-mono text-xs">/p/{s.handle}</TableCell>
                    <TableCell><Badge data-testid={T.status(s.id)} variant="outline" className={`uppercase text-[10px] tracking-[0.1em] ${tone[s.status]}`}>{s.status}</Badge></TableCell>
                    <TableCell>
                      {s.ai_screening ? (
                        <div className="flex items-center gap-1.5" data-testid={`admin-photographer-screening-${s.id}`}>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.08em] ${screeningTone[s.ai_screening.quality] || screeningTone.medium}`}>{s.ai_screening.quality}</Badge>
                          {s.screening_score != null && (
                            <span data-testid={T.scoreBadge(s.id)} className="text-[10px] text-[hsl(var(--lxra-muted))] lxra-mono" title="Composite AI screening score (0-100)">{s.screening_score}</span>
                          )}
                          {s.ai_screening.spam_likelihood >= 0.6 && <ShieldAlert className="h-3.5 w-3.5 text-red-400" title="Flagged as possible spam" />}
                        </div>
                      ) : (
                        <span className="text-[11px] text-[hsl(var(--lxra-muted))]">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge data-testid={T.decisionBadge(s.id)} variant="outline" className={`text-[10px] uppercase tracking-[0.08em] whitespace-nowrap ${decision.className}`} title={s.reason || ''}>
                        {decision.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={Boolean(s.verified)} disabled={busy === s.id} onCheckedChange={(v) => moderate(s, { verified: v }, v ? `${s.name} is now Verified LXRA.` : `Verified badge removed from ${s.name}.`)} data-testid={T.verifiedToggle(s.id)} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
                    </TableCell>
                    <TableCell className="text-xs text-[hsl(var(--lxra-muted))] max-w-[160px] truncate">{s.home_base?.label || '—'}</TableCell>
                    <TableCell className="text-xs text-[hsl(var(--lxra-fg))]">{formatPriceFrom(s.price_from_cents) || '—'}</TableCell>
                    <TableCell className="text-[11px] text-[hsl(var(--lxra-muted))]">{s.counts?.galleries || 0} gal · {s.counts?.packages || 0} pkg · {s.counts?.bookings || 0} bk</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {busy === s.id && <Loader2 className="inline h-4 w-4 animate-spin text-[hsl(var(--lxra-gold))] mr-1" />}
                      {s.status !== 'approved' && (
                        <Button size="sm" onClick={() => moderate(s, { status: 'approved' }, `${s.name} approved — their profile is live.`)} disabled={busy === s.id} data-testid={s.status === 'pending' ? T.approve(s.id) : T.reinstate(s.id)} className="h-8 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-[10px] uppercase tracking-[0.1em] font-semibold mr-1">
                          <Check className="h-3.5 w-3.5 mr-1" /> {s.status === 'pending' ? 'Approve' : 'Reinstate'}
                        </Button>
                      )}
                      {s.status === 'approved' && !isLxra && (
                        <Button size="icon" variant="ghost" onClick={() => setConfirm({ studio: s, action: 'suspend' })} data-testid={T.suspend(s.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10" title="Suspend"><Pause className="h-4 w-4" /></Button>
                      )}
                      {s.status === 'approved' && (
                        <Button size="icon" variant="ghost" asChild data-testid={T.viewProfile(s.id)} className={iconBtn} title="Open public profile"><Link to={`/p/${s.handle}`} target="_blank"><ExternalLink className="h-4 w-4" /></Link></Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => viewAs(s)} data-testid={T.viewAs(s.id)} className={iconBtn} title="View dashboard as this studio"><Eye className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setReviewsFor(s)} data-testid={T.reviewsButton(s.id)} className={iconBtn} title="View reviews"><Star className="h-4 w-4" /></Button>
                      {!isLxra && s.status !== 'approved' && (
                        <Button size="icon" variant="ghost" onClick={() => setConfirm({ studio: s, action: 'remove' })} data-testid={T.remove(s.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10" title="Remove studio"><Trash2 className="h-4 w-4" /></Button>
                      )}
                      {s.status === 'suspended' && <Play className="hidden" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.action === 'remove' ? `Remove ${confirm?.studio.name}?` : `Suspend ${confirm?.studio.name}?`}</AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(var(--lxra-muted))]">
              {confirm?.action === 'remove'
                ? 'This deletes the studio record and its login. Only possible for studios that never went live and hold no data.'
                : 'Their public profile and booking link go offline immediately. Their data is kept and you can reinstate them at any time.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-photographers-confirm-cancel" className="border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] hover:bg-[#141416]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed} data-testid="admin-photographers-confirm-action" className="bg-red-500/90 text-white hover:bg-red-500">{confirm?.action === 'remove' ? 'Remove' : 'Suspend'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AdminReviewsDialog studio={reviewsFor} open={Boolean(reviewsFor)} onOpenChange={(o) => !o && setReviewsFor(null)} />
    </div>
  );
};
